import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  DisplayPanelCount,
  TradeshowDisplaySelection,
  TradeshowOrderFormData,
  TradeshowOrderRecord,
} from '../@types/tradeshowOrder';

const COLLECTION = 'tradeshow-orders';

export const PANEL_LABELS: Record<DisplayPanelCount, string> = {
  '1': '1 panel',
  '2': '2 panels',
  '3': '3 panels',
  '4': '4 panels',
};

export function formatDisplaySummary(
  displays?: TradeshowDisplaySelection[],
  legacyPanels?: string
): string {
  if (displays && displays.length > 0) {
    return displays
      .map((item) => `${item.quantity} × ${PANEL_LABELS[item.panels] || `${item.panels} panel`}`)
      .join(', ');
  }
  if (legacyPanels && PANEL_LABELS[legacyPanels as DisplayPanelCount]) {
    return PANEL_LABELS[legacyPanels as DisplayPanelCount];
  }
  return legacyPanels || '';
}

export class TradeshowOrderService {
  static async submitOrder(
    form: TradeshowOrderFormData,
    _options: { companyWebsite?: string; formOpenedAt: string; recaptchaToken?: string; language?: string }
  ): Promise<{
    orderId: string;
    emailSent: boolean;
  }> {
    if (form.displays.length === 0) {
      throw new Error('Please select at least one display.');
    }
    if (!form.poNumber.trim()) {
      throw new Error('Enter a PO number.');
    }
    if (form.displays.some((item) => !Number.isInteger(item.quantity) || item.quantity < 1)) {
      throw new Error('Enter how many of each selected display you need.');
    }

    const displays = form.displays;
    const docRef = await addDoc(collection(db, COLLECTION), {
      storeName: form.storeName.trim(),
      storeNumber: form.storeNumber.trim(),
      storeAddress: form.storeAddress.trim(),
      contactName: form.contactName.trim(),
      email: form.email.trim(),
      poNumber: form.poNumber.trim(),
      deliveryDate: form.deliveryDate.trim(),
      accountManagerNotes: form.accountManagerNotes.trim(),
      displays,
      displaySummary: formatDisplaySummary(displays),
      status: 'submitted',
      source: 'tradeshow',
      emailSent: false,
      createdAt: serverTimestamp(),
    });

    return {
      orderId: docRef.id,
      emailSent: false,
    };
  }

  static async getAllSubmissions(): Promise<TradeshowOrderRecord[]> {
    const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as TradeshowOrderRecord[];
  }

  static async markReviewed(orderId: string, reviewedBy: string): Promise<void> {
    await updateDoc(doc(db, COLLECTION, orderId), {
      status: 'reviewed',
      reviewedAt: serverTimestamp(),
      reviewedBy,
    });
  }

  static async markSubmitted(orderId: string): Promise<void> {
    await updateDoc(doc(db, COLLECTION, orderId), {
      status: 'submitted',
      reviewedAt: null,
      reviewedBy: null,
    });
  }
}
